<template>
  <div v-if="shouldShowAdSpace" :class="wrapperClasses">
    <!-- Google Ad Component -->
    <GoogleAd
      v-if="canShowGoogleAd"
      :ad-type="adType"
      :class-name="className"
      :show-label="showLabel"
      :min-content-height="minContentHeight"
      @ad-loaded="onAdLoaded"
      @ad-failed="onAdFailed"
    />
    
    <!-- Fallback: Show placeholder only if explicitly requested -->
    <AdPlaceholder
      v-else-if="showPlaceholder"
      :ad-type="adType"
      :class-name="className"
    />
    
    <!-- Debug info (development only) -->
    <div v-if="isDevelopment && showDebug" class="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900 rounded text-xs">
      <div>AdSense Enabled: {{ adSenseEnabled }}</div>
      <div>User Consent: {{ hasAdConsent }}</div>
      <div>Content Height: {{ contentHeight }}px (min: {{ minContentHeight }}px)</div>
      <div>Ad Type: {{ adType }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useConsentStore } from '@/stores/consentStore';
import { adSenseConfig } from '@/services/adSenseConfig';
import GoogleAd from './GoogleAd.vue';
import AdPlaceholder from './AdPlaceholder.vue';

interface Props {
  adType: 'square' | 'infeed' | 'banner' | 'rectangle';
  className?: string;
  showLabel?: boolean;
  minContentHeight?: number;
  showPlaceholder?: boolean; // Whether to show placeholder when ads can't be shown
  showDebug?: boolean; // Show debug info in development
}

const props = withDefaults(defineProps<Props>(), {
  className: '',
  showLabel: true,
  minContentHeight: 300,
  showPlaceholder: false, // Default to false to avoid blank spaces
  showDebug: false
});

// State
const contentHeight = ref(0);
const adLoaded = ref(false);
const adFailed = ref(false);

// Composables
const consentStore = useConsentStore();

// Computed
const isDevelopment = computed(() => process.env.NODE_ENV === 'development');

const adSenseEnabled = computed(() => adSenseConfig.isEnabled());

const hasAdConsent = computed(() => consentStore.canUseAdvertising);

const hasMinimumContent = computed(() => contentHeight.value >= props.minContentHeight);

const canShowGoogleAd = computed(() => 
  adSenseEnabled.value && 
  hasAdConsent.value && 
  hasMinimumContent.value &&
  !adFailed.value
);

const shouldShowAdSpace = computed(() => 
  canShowGoogleAd.value || (props.showPlaceholder && hasMinimumContent.value)
);

const wrapperClasses = computed(() => [
  'smart-ad-wrapper',
  props.className,
  {
    'ad-space-active': canShowGoogleAd.value,
    'ad-space-placeholder': props.showPlaceholder && !canShowGoogleAd.value
  }
]);

// Methods
const updateContentHeight = () => {
  contentHeight.value = document.documentElement.scrollHeight;
};

const onAdLoaded = () => {
  adLoaded.value = true;
  adFailed.value = false;
};

const onAdFailed = () => {
  adFailed.value = true;
  adLoaded.value = false;
};

// Lifecycle
onMounted(() => {
  updateContentHeight();
  
  // Update content height when window resizes
  window.addEventListener('resize', updateContentHeight);
  
  // Update content height when content changes (with debounce)
  let resizeTimeout: number;
  const observer = new MutationObserver(() => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateContentHeight, 100);
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true
  });
  
  // Cleanup
  return () => {
    window.removeEventListener('resize', updateContentHeight);
    observer.disconnect();
    clearTimeout(resizeTimeout);
  };
});
</script>

<style scoped>
.smart-ad-wrapper {
  @apply transition-all duration-300;
}

.ad-space-active {
  @apply opacity-100;
}

.ad-space-placeholder {
  @apply opacity-90;
}

/* Ensure smooth transitions */
.smart-ad-wrapper > * {
  @apply transition-opacity duration-300;
}
</style>